import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { History, Plus, Check, Edit2, X, AlertTriangle } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import {
  PageError,
  PageLoading,
  PageNotFound,
} from '@/components/ui/PageAsyncState'
import {
  packagingBomService,
  type PackagingBom,
  type PackagingBomDetail,
  type PackagingBomLine,
} from '@/services/packagingBoms'

export interface PackagingBomDetailPageProps {
  initialDetail?: PackagingBomDetail | null
}

export function PackagingBomDetailPage({
  initialDetail = null,
}: PackagingBomDetailPageProps = {}) {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()

  const [detail, setDetail] = useState<PackagingBomDetail | null>(initialDetail)
  const [loading, setLoading] = useState(!initialDetail)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  // Edit mode for active or draft
  const [isEditing, setIsEditing] = useState(false)
  const [editedLines, setEditedLines] = useState<PackagingBomLine[]>([])
  const [conflictError, setConflictError] = useState<string | null>(null)

  // Drawer "BOM cũ"
  const [showHistoryDrawer, setShowHistoryDrawer] = useState(false)
  const [familyVersions, setFamilyVersions] = useState<PackagingBom[]>([])
  const [historyLoading, setHistoryLoading] = useState(false)
  const [compareResult, setCompareResult] = useState<any | null>(null)

  // New line draft state
  const [showAddLineModal, setShowAddLineModal] = useState(false)
  const [newLineName, setNewLineName] = useState('')
  const [newLineSpec, setNewLineSpec] = useState('')
  const [newLineQty, setNewLineQty] = useState('1')
  const [newLineUom, setNewLineUom] = useState('Bộ')
  const [newLineNotes, setNewLineNotes] = useState('')

  const loadDetail = useCallback(async () => {
    if (!id) return
    setLoading(true)
    setError(null)
    setConflictError(null)
    try {
      const data = await packagingBomService.getById(id)
      setDetail(data)
      if (data) {
        setEditedLines([...data.lines])
      }
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Không tải được chi tiết Bộ đóng gói',
      )
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    void loadDetail()
  }, [loadDetail])

  // Activate
  async function handleActivate() {
    if (!detail) return
    setBusy(true)
    setError(null)
    try {
      await packagingBomService.activate(detail.id)
      await loadDetail()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Kích hoạt Bộ đóng gói thất bại',
      )
    } finally {
      setBusy(false)
    }
  }

  // Add line to inactive draft
  async function handleAddLine(e: React.FormEvent) {
    e.preventDefault()
    if (!detail || !newLineName.trim()) return
    setBusy(true)
    try {
      await packagingBomService.createLine(detail.id, {
        name_raw: newLineName.trim(),
        spec_raw: newLineSpec.trim() || null,
        qty_per: Number(newLineQty) || 1,
        uom_hint: newLineUom.trim() || 'Bộ',
        notes: newLineNotes.trim() || null,
        sort_order: (detail.lines.length || 0) + 1,
      })
      setShowAddLineModal(false)
      setNewLineName('')
      setNewLineSpec('')
      setNewLineQty('1')
      setNewLineUom('Bộ')
      setNewLineNotes('')
      await loadDetail()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Thêm dòng đóng gói thất bại',
      )
    } finally {
      setBusy(false)
    }
  }

  // Delete line from inactive draft
  async function handleDeleteLine(lineId: string) {
    if (!detail) return
    if (detail.status === 'active' && isEditing) {
      setEditedLines((prev) => prev.filter((l) => l.id !== lineId))
      return
    }
    setBusy(true)
    try {
      await packagingBomService.deleteLine(detail.id, lineId)
      await loadDetail()
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Xóa dòng đóng gói thất bại',
      )
    } finally {
      setBusy(false)
    }
  }

  // Save active version with optimistic locking
  async function handleSaveActive() {
    if (!detail) return
    setBusy(true)
    setConflictError(null)
    try {
      const next = await packagingBomService.saveActive(detail.id, {
        name: detail.name,
        notes: detail.notes,
        base_version_seq: detail.version_seq,
        lines: editedLines.map((l, idx) => ({
          id: l.id.startsWith('temp-') ? undefined : l.id,
          name_raw: l.name_raw,
          spec_raw: l.spec_raw,
          material_id: l.material_id,
          qty_per: l.qty_per,
          uom_hint: l.uom_hint,
          notes: l.notes,
          sort_order: idx + 1,
        })),
      })
      setIsEditing(false)
      if (next.id !== detail.id) {
        navigate(`/purchasing/packaging-boms/${next.id}`)
      } else {
        await loadDetail()
      }
    } catch (err: any) {
      if (err.status === 409 || err.code === 'BOM_VERSION_CONFLICT') {
        setConflictError(
          'BOM đã có phiên bản mới được lưu bởi người khác. Vui lòng tải lại dữ liệu trước khi tiếp tục sửa.',
        )
      } else {
        setError(
          err instanceof Error
            ? err.message
            : 'Lưu phiên bản Bộ đóng gói thất bại',
        )
      }
    } finally {
      setBusy(false)
    }
  }

  // Load history drawer
  async function openHistoryDrawer() {
    if (!detail) return
    setShowHistoryDrawer(true)
    setHistoryLoading(true)
    setCompareResult(null)
    try {
      const list = await packagingBomService.getFamilyVersions(
        detail.packaging_bom_family_id,
      )
      setFamilyVersions(list)
    } catch (err) {
      console.error(err)
    } finally {
      setHistoryLoading(false)
    }
  }

  // Compare with a target version
  async function handleCompare(targetId: string) {
    if (!detail) return
    try {
      const res = await packagingBomService.compare(detail.id, targetId)
      setCompareResult(res)
    } catch (err) {
      console.error(err)
    }
  }

  if (loading) return <PageLoading label="Đang tải Bộ đóng gói…" />

  if (!detail) {
    return (
      <PageNotFound
        title="Không tìm thấy Bộ đóng gói"
        description={error ?? 'Bộ đóng gói không tồn tại hoặc đã bị xóa.'}
        action={
          <Button onClick={() => navigate('/purchasing/packaging-boms')}>
            Về danh sách
          </Button>
        }
      />
    )
  }

  const currentLines = isEditing ? editedLines : detail.lines

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          {
            label: 'Bộ đóng gói (Packaging BOM)',
            to: '/purchasing/packaging-boms',
          },
          { label: detail.name },
        ]}
      />

      <PageHeader
        title={`${detail.name} (v${detail.version_seq}${detail.edit_count ? ` · Đã sửa lần ${detail.edit_count}` : ''})`}
        subtitle={`Trạng thái: ${detail.status.toUpperCase()} · Code hint: ${detail.code_hint || '—'} · Cập nhật: ${new Date(detail.updated_at).toLocaleString('vi-VN')}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => void openHistoryDrawer()}
            >
              <History className="h-4 w-4" />
              <span>BOM cũ ({familyVersions.length || 'Lịch sử'})</span>
            </Button>

            {detail.status === 'inactive' ? (
              <>
                <Button
                  variant="secondary"
                  disabled={busy}
                  onClick={() => setShowAddLineModal(true)}
                >
                  <Plus className="h-4 w-4" />
                  Thêm dòng
                </Button>
                <Button disabled={busy} onClick={() => void handleActivate()}>
                  <Check className="h-4 w-4" />
                  Kích hoạt (ACTIVE)
                </Button>
              </>
            ) : detail.status === 'active' ? (
              isEditing ? (
                <>
                  <Button
                    variant="secondary"
                    disabled={busy}
                    onClick={() => {
                      setIsEditing(false)
                      setEditedLines([...detail.lines])
                      setConflictError(null)
                    }}
                  >
                    Hủy
                  </Button>
                  <Button
                    disabled={busy}
                    onClick={() => void handleSaveActive()}
                  >
                    {busy ? 'Đang lưu…' : 'Lưu phiên bản mới'}
                  </Button>
                </>
              ) : (
                <Button onClick={() => setIsEditing(true)}>
                  <Edit2 className="h-4 w-4" />
                  Sửa tại chỗ
                </Button>
              )
            ) : (
              <span className="text-xs text-slate-500 italic">
                Bản lưu trữ (chỉ-đọc)
              </span>
            )}
          </div>
        }
      />

      {error ? <PageError message={error} /> : null}

      {conflictError ? (
        <div className="rounded-lg border border-rose-300 bg-rose-50 p-4 text-xs text-rose-900 shadow-sm flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-rose-600 shrink-0" />
          <div className="flex-1">
            <p className="font-bold text-sm">Xung đột phiên bản (409 Conflict)</p>
            <p className="mt-1">{conflictError}</p>
            <div className="mt-3">
              <Button
                variant="secondary"
                onClick={() => void loadDetail()}
              >
                Tải lại dữ liệu mới nhất
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Lines Table */}
      <section className="overflow-x-auto rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-surface)] shadow-[var(--shadow-level-1)]">
        <div className="p-3 border-b border-slate-100 flex items-center justify-between">
          <span className="text-xs font-semibold text-slate-800">
            Danh sách quy cách đóng gói ({currentLines.length} dòng)
          </span>
          {isEditing ? (
            <span className="text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded border border-amber-200">
              Đang ở chế độ sửa tại chỗ — lưu để tự tạo phiên bản mới
            </span>
          ) : null}
        </div>

        <table className="min-w-full text-left text-xs">
          <thead className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase text-slate-500">
            <tr>
              <th className="px-3 py-2 w-12">STT</th>
              <th className="px-3 py-2">Tên vật tư đóng gói</th>
              <th className="px-3 py-2">Quy cách kỹ thuật</th>
              <th className="px-3 py-2 text-right w-24">Số lượng</th>
              <th className="px-3 py-2 w-20">ĐVT</th>
              <th className="px-3 py-2">Ghi chú</th>
              {isEditing || detail.status === 'inactive' ? (
                <th className="px-3 py-2 text-right w-20">Thao tác</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {currentLines.map((line, idx) => (
              <tr key={line.id} className="border-t border-slate-100">
                <td className="px-3 py-2 text-slate-400 font-mono">
                  {idx + 1}
                </td>
                <td className="px-3 py-2 font-medium text-slate-900">
                  {isEditing ? (
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      value={line.name_raw}
                      onChange={(e) => {
                        const val = e.target.value
                        setEditedLines((prev) =>
                          prev.map((l) =>
                            l.id === line.id ? { ...l, name_raw: val } : l,
                          ),
                        )
                      }}
                    />
                  ) : (
                    line.name_raw
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {isEditing ? (
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      value={line.spec_raw || ''}
                      onChange={(e) => {
                        const val = e.target.value
                        setEditedLines((prev) =>
                          prev.map((l) =>
                            l.id === line.id ? { ...l, spec_raw: val } : l,
                          ),
                        )
                      }}
                    />
                  ) : (
                    line.spec_raw || '—'
                  )}
                </td>
                <td className="px-3 py-2 text-right font-mono">
                  {isEditing ? (
                    <input
                      type="number"
                      className="w-20 rounded border border-slate-300 px-2 py-1 text-right text-xs"
                      value={line.qty_per}
                      onChange={(e) => {
                        const val = Number(e.target.value) || 0
                        setEditedLines((prev) =>
                          prev.map((l) =>
                            l.id === line.id ? { ...l, qty_per: val } : l,
                          ),
                        )
                      }}
                    />
                  ) : (
                    line.qty_per
                  )}
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {isEditing ? (
                    <input
                      className="w-16 rounded border border-slate-300 px-2 py-1 text-xs"
                      value={line.uom_hint || ''}
                      onChange={(e) => {
                        const val = e.target.value
                        setEditedLines((prev) =>
                          prev.map((l) =>
                            l.id === line.id ? { ...l, uom_hint: val } : l,
                          ),
                        )
                      }}
                    />
                  ) : (
                    line.uom_hint || '—'
                  )}
                </td>
                <td className="px-3 py-2 text-slate-500">
                  {isEditing ? (
                    <input
                      className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
                      value={line.notes || ''}
                      onChange={(e) => {
                        const val = e.target.value
                        setEditedLines((prev) =>
                          prev.map((l) =>
                            l.id === line.id ? { ...l, notes: val } : l,
                          ),
                        )
                      }}
                    />
                  ) : (
                    line.notes || '—'
                  )}
                </td>
                {isEditing || detail.status === 'inactive' ? (
                  <td className="px-3 py-2 text-right">
                    <button
                      type="button"
                      className="text-rose-600 hover:text-rose-800 text-xs font-medium"
                      onClick={() => void handleDeleteLine(line.id)}
                    >
                      Xóa
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
            {currentLines.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="px-3 py-8 text-center text-slate-400 italic"
                >
                  Chưa có dòng quy cách đóng gói nào.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </section>

      {/* Drawer BOM cũ */}
      {showHistoryDrawer ? (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="h-full w-full max-w-md bg-white shadow-2xl p-6 overflow-y-auto space-y-4">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <div>
                <h3 className="text-base font-bold text-slate-900">
                  Lịch sử phiên bản (BOM cũ)
                </h3>
                <p className="text-xs text-slate-500">
                  Mã Family: {detail.packaging_bom_family_id}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowHistoryDrawer(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {historyLoading ? (
              <p className="text-xs text-slate-400 text-center py-6">
                Đang tải lịch sử phiên bản…
              </p>
            ) : (
              <div className="space-y-3">
                {familyVersions.map((v) => (
                  <div
                    key={v.id}
                    className={`rounded-lg border p-3 text-xs space-y-1.5 ${
                      v.id === detail.id
                        ? 'border-indigo-400 bg-indigo-50/50'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">
                        {v.version_label} (v{v.version_seq})
                      </span>
                      <StatusBadge
                        tone={v.status === 'active' ? 'success' : 'neutral'}
                        label={v.status.toUpperCase()}
                        showDot={false}
                      />
                    </div>
                    <p className="text-slate-500 text-xs">
                      Sửa bởi:{' '}
                      <strong>{v.edited_by || v.updated_by || 'system'}</strong>
                      {v.edited_at ? ` · ${new Date(v.edited_at).toLocaleString('vi-VN')}` : ''}
                    </p>
                    {v.edit_note ? (
                      <p className="text-slate-700 italic">
                        "{v.edit_note}"
                      </p>
                    ) : null}
                    <div className="pt-2 flex items-center justify-between border-t border-slate-100">
                      {v.id !== detail.id ? (
                        <button
                          type="button"
                          className="text-indigo-600 hover:underline font-medium text-xs"
                          onClick={() => void handleCompare(v.id)}
                        >
                          So sánh với bản hiện tại
                        </button>
                      ) : (
                        <span className="text-xs text-slate-400">
                          (Bản đang mở)
                        </span>
                      )}
                      <button
                        type="button"
                        className="text-slate-600 hover:underline text-xs"
                        onClick={() => {
                          setShowHistoryDrawer(false)
                          navigate(`/purchasing/packaging-boms/${v.id}`)
                        }}
                      >
                        Mở bản này →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Compare Result */}
            {compareResult ? (
              <div className="mt-4 rounded-lg border border-indigo-200 bg-indigo-50/40 p-3 text-xs space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-bold text-indigo-900">
                    Kết quả so sánh phiên bản:
                  </span>
                  <button
                    type="button"
                    onClick={() => setCompareResult(null)}
                    className="text-indigo-600 text-xs hover:underline"
                  >
                    Đóng
                  </button>
                </div>
                <div className="text-xs text-slate-600">
                  Dòng thêm: <strong>{compareResult.diffs?.added?.length || 0}</strong> ·
                  Dòng bớt: <strong>{compareResult.diffs?.removed?.length || 0}</strong> ·
                  Dòng sửa: <strong>{compareResult.diffs?.modified?.length || 0}</strong>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      {/* Modal Thêm dòng */}
      {showAddLineModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">
                Thêm dòng quy cách đóng gói
              </h3>
              <button
                type="button"
                onClick={() => setShowAddLineModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => void handleAddLine(e)} className="space-y-3 text-xs">
              <label className="block">
                <span className="font-semibold text-slate-700">Tên vật tư *</span>
                <input
                  required
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  value={newLineName}
                  onChange={(e) => setNewLineName(e.target.value)}
                  placeholder="VD: Thùng carton 5 lớp..."
                />
              </label>

              <label className="block">
                <span className="font-semibold text-slate-700">Quy cách kỹ thuật</span>
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  value={newLineSpec}
                  onChange={(e) => setNewLineSpec(e.target.value)}
                  placeholder="VD: 1200x600x400 mm"
                />
              </label>

              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="font-semibold text-slate-700">Số lượng</span>
                  <input
                    type="number"
                    min="0.0001"
                    step="any"
                    required
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                    value={newLineQty}
                    onChange={(e) => setNewLineQty(e.target.value)}
                  />
                </label>
                <label className="block">
                  <span className="font-semibold text-slate-700">Đơn vị (ĐVT)</span>
                  <input
                    className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                    value={newLineUom}
                    onChange={(e) => setNewLineUom(e.target.value)}
                  />
                </label>
              </div>

              <label className="block">
                <span className="font-semibold text-slate-700">Ghi chú</span>
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  value={newLineNotes}
                  onChange={(e) => setNewLineNotes(e.target.value)}
                  placeholder="VD: Dán băng keo niêm phong..."
                />
              </label>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowAddLineModal(false)}
                  disabled={busy}
                >
                  Hủy
                </Button>
                <Button type="submit" disabled={busy || !newLineName.trim()}>
                  {busy ? 'Đang thêm…' : 'Thêm dòng'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  )
}
